import React, { useState } from 'react';
import MarcEtapa from './MarcEtapa';
import { useTranslation } from 'react-i18next';
import {
  PlusIcon,
  XMarkIcon,
  LightBulbIcon,
  UserGroupIcon,
  ArrowsRightLeftIcon,
  EyeIcon,
  UserIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';

const RootDefinitionsStage = ({ data, onUpdate, onNext }) => {
  const { t } = useTranslation();
  const [rootDefinitions, setRootDefinitions] = useState(data.rootDefinitions || []);
  // NOMÉS L'IDENTIFICADOR A L'ESTAT, i l'objecte es dedueix de la llista a cada render.
  //
  // Abans aquí hi havia una CÒPIA de la definició arrel, i no es refeia mai en editar-la:
  // escrivies una lletra, anava a la llista, i el camp seguia llegint la còpia vella —o sigui
  // que **només es podia escriure un caràcter i el següent el substituïa**. Amb l'objecte deduït,
  // el que es veu és sempre el que hi ha.
  const [selectedRDId, setSelectedRDId] = useState(null);
  const selectedRD = rootDefinitions.find((rd) => rd.id === selectedRDId) || null;
  const setSelectedRD = (rd) => setSelectedRDId(rd?.id ?? null);
  const [showForm, setShowForm] = useState(false);
  const [avisGenerar, setAvisGenerar] = useState(false);

  // CATWOE. **L'acrònim es queda en anglès i és a posta**: és el mnemotècnic de la disciplina, i
  // la mateixa Viquipèdia catalana s'ha de contorsionar —prOpietari, limitacions ambiEntals— per
  // fer-lo quadrar. El que es tradueix són els noms i les descripcions.
  const catwoeElements = [
    { key: 'customers', letter: 'C', nom: 'catC', desc: 'catCd', icon: UserGroupIcon, color: 'text-primary-600' },
    { key: 'actors', letter: 'A', nom: 'catA', desc: 'catAd', icon: UserIcon, color: 'text-ok-600' },
    { key: 'transformation', letter: 'T', nom: 'catT', desc: 'catTd', icon: ArrowsRightLeftIcon, color: 'text-primary-600' },
    { key: 'worldview', letter: 'W', nom: 'catW', desc: 'catWd', icon: EyeIcon, color: 'text-warn-600' },
    { key: 'owners', letter: 'O', nom: 'catO', desc: 'catOd', icon: UserGroupIcon, color: 'text-danger-600' },
    { key: 'environment', letter: 'E', nom: 'catE', desc: 'catEd', icon: GlobeAltIcon, color: 'text-primary-600' },
  ];

  const createNewRootDefinition = () => {
    const newRD = {
      id: Date.now().toString(),
      systemName: '',
      rootDefinition: '',
      customers: [],
      actors: [],
      transformation: { input: '', output: '', process: '' },
      worldview: '',
      owners: [],
      environment: { constraints: [], enablers: [], context: '' },
      priority: rootDefinitions.length + 1,
      isActive: true
    };
    
    setRootDefinitions([...rootDefinitions, newRD]);
    setSelectedRD(newRD);
    setShowForm(true);
  };

  const updateRootDefinition = (updates) => {
    setAvisGenerar(false);
    const updated = rootDefinitions.map(rd => 
      rd.id === selectedRD.id ? { ...rd, ...updates } : rd
    );
    setRootDefinitions(updated);
    onUpdate({ rootDefinitions: updated });
  };

  // ESBORRAR-NE UNA SE'N DUU EL SEU MODEL CONCEPTUAL, i ho pregunta abans.
  //
  // Cada model conceptual de l'etapa 4 penja d'UNA definició arrel. Deixar-lo orfe seria una
  // pantalla en blanc que ningú no sabria explicar; esborrar-lo en silenci seria perdre una hora
  // de feina amb un clic. Es diu quantes activitats se'n van i es demana.
  //
  // És el mateix problema que `arrossegaEnEsborrar` al llenç d'estocs, un document més avall.
  const deleteRootDefinition = (id) => {
    const conceptuals = data.conceptualModels || [];
    const seu = conceptuals.find((m) => m.rootDefinitionId === id);
    const quantes = (seu?.activities || []).filter((a) => a.type !== 'monitoring').length;
    if (seu && !window.confirm(t('ssm.esborrarRD', { n: quantes }))) return;

    const updated = rootDefinitions.filter(rd => rd.id !== id);
    setRootDefinitions(updated);
    onUpdate({
      rootDefinitions: updated,
      ...(seu ? { conceptualModels: conceptuals.filter((m) => m.rootDefinitionId !== id) } : {}),
    });
    if (selectedRD?.id === id) {
      setSelectedRD(null);
      setShowForm(false);
    }
  };

  // LA FRASE LA MUNTA EL DICCIONARI, no el codi.
  //
  // Abans es construïa amb una plantilla anglesa escrita aquí mateix, o sigui que **un model fet
  // en català sortia amb la definició arrel en anglès**. I no és cap detall: la definició arrel és
  // el text que després es compara amb el món a l'etapa 5.
  //
  // I prova primer el PQR, que és la forma canònica: qui l'ha omplert ja ha dit què, com i per a
  // què, i el CATWOE és el pla B.
  const generateRootDefinition = () => {
    if (!selectedRD) return;
    const { transformation = {}, actors, customers, worldview, p, q, r } = selectedRD;
    const llista = (v) => (Array.isArray(v) ? v.filter(Boolean).join(', ') : v || '');
    const net = (v) => String(v || '').trim();

    if (net(p) && net(q) && net(r)) {
      updateRootDefinition({ rootDefinition: t('ssm.generatedPQR', { p: net(p), q: net(q), r: net(r) }) });
      return;
    }
    const proces = net(transformation.process)
      || (net(transformation.input) && net(transformation.output)
        ? `${net(transformation.input)} → ${net(transformation.output)}` : '');
    if (!proces && !net(worldview)) { setAvisGenerar(true); return; }
    updateRootDefinition({
      rootDefinition: t('ssm.generatedCatwoe', {
        actors: llista(actors), transformation: proces,
        customers: llista(customers), worldview: net(worldview),
      }),
    });
  };

  return (
    <MarcEtapa propi>
      {/* Root Definitions List */}
      {/* Prima a posta: aquí només hi ha la llista de definicions arrel i el botó d'afegir-ne.
          Els 384 px d'abans els necessita el formulari del costat, on s'hi escriuen els clients,
          la visió del món i la resta. */}
      <div className="w-64 flex-shrink-0 min-h-0 overflow-y-auto bg-white border-r border-gray-200 p-3">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900">{t('ssm.rootDefinitions')}</h3>
          <button
            onClick={createNewRootDefinition}
            title={t('ssm.afegir')}
            className="p-1.5 bg-primary-700 text-white rounded-md hover:bg-primary-800 flex-shrink-0"
          >
            <PlusIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3 mb-6">
          {rootDefinitions.map((rd, index) => (
            <div
              key={rd.id}
              onClick={() => {
                setSelectedRD(rd);
                setShowForm(true);
              }}
              className={`
                p-4 border-2 rounded-md cursor-pointer transition-colors
                ${selectedRD?.id === rd.id 
                  ? 'border-primary-200 bg-primary-50' 
                  : 'border-gray-200 hover:border-gray-300'
                }
              `}
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900">
                  {rd.systemName || t('ssm.rdN', { n: index + 1 })}
                </h4>
                <div className="flex items-center space-x-1">
                  <span className="text-xs text-gray-500">{t('ssm.priority', { n: rd.priority })}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteRootDefinition(rd.id);
                    }}
                    className="text-gray-500 hover:text-danger-500"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <p className="text-xs leading-snug text-gray-600 line-clamp-5">
                {rd.rootDefinition || t('ssm.senseDefinicio')}
              </p>
            </div>
          ))}
        </div>

        {rootDefinitions.length === 0 && (
          <div className="text-center py-8">
            <LightBulbIcon className="h-12 w-12 text-gray-500 mx-auto mb-4" />
            <p className="text-gray-500 text-sm">
              {t('ssm.noneYet')}
            </p>
          </div>
        )}

        {/* Instructions */}
        <div className="p-3 bg-primary-50 border border-primary-200 rounded-md">
          <h4 className="text-sm font-medium text-primary-800 mb-2">{t('ssm.catwoeGuide')}</h4>
          <p className="text-xs text-primary-700">
            {t('ssm.pickOneWhy')}
          </p>
        </div>
      </div>

      {/* CATWOE Analysis Form */}
      {showForm && selectedRD && (
        <div className="flex-1 min-w-0 min-h-0 overflow-y-auto p-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white border border-gray-200 rounded-md p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {t('ssm.catwoeOf', { nom: selectedRD.systemName || t('ssm.untitled') })}
              </h2>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('ssm.systemNameLabel')}
                </label>
                <input
                  type="text"
                  value={selectedRD.systemName}
                  onChange={(e) => updateRootDefinition({ systemName: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder={t('catwoe.systemName')}
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {catwoeElements.map((element) => {
                  const Icon = element.icon;
                  return (
                    <div key={element.key} className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <div className={`p-2 rounded-md bg-gray-100`}>
                          <Icon className={`h-5 w-5 ${element.color}`} />
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">
                            ({element.letter}) {t(`ssm.${element.nom}`)}
                          </h3>
                          <p className="text-xs text-gray-600">{t(`ssm.${element.desc}`)}</p>
                        </div>
                      </div>
                      
                      {element.key === 'transformation' ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            placeholder={t('catwoe.input')}
                            value={selectedRD.transformation?.input || ''}
                            onChange={(e) => updateRootDefinition({
                              transformation: { ...selectedRD.transformation, input: e.target.value }
                            })}
                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                          />
                          <input
                            type="text"
                            placeholder={t('catwoe.process')}
                            value={selectedRD.transformation?.process || ''}
                            onChange={(e) => updateRootDefinition({
                              transformation: { ...selectedRD.transformation, process: e.target.value }
                            })}
                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                          />
                          <input
                            type="text"
                            placeholder={t('catwoe.output')}
                            value={selectedRD.transformation?.output || ''}
                            onChange={(e) => updateRootDefinition({
                              transformation: { ...selectedRD.transformation, output: e.target.value }
                            })}
                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                          />
                        </div>
                      ) : element.key === 'environment' ? (
                        <textarea
                          placeholder={t('catwoe.environment')}
                          value={selectedRD.environment?.context || ''}
                          onChange={(e) => updateRootDefinition({
                            environment: { ...selectedRD.environment, context: e.target.value }
                          })}
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                          rows="3"
                        />
                      ) : (
                        <textarea
                          placeholder={t(`ssm.${element.nom}`)}
                          value={Array.isArray(selectedRD[element.key]) 
                            ? selectedRD[element.key].join(', ')
                            : selectedRD[element.key] || ''
                          }
                          onChange={(e) => {
                            const value = ['customers', 'actors', 'owners'].includes(element.key)
                              ? e.target.value.split(',').map(s => s.trim()).filter(s => s)
                              : e.target.value;
                            updateRootDefinition({ [element.key]: value });
                          }}
                          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                          rows="2"
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* PQR. La forma canònica d'una definició arrel: **fer P per mitjà de Q per
                  aconseguir R**. Hi és abans del text lliure i no després, perquè el text lliure
                  surt d'aquí: qui escriu la definició sense passar per la R acaba dient QUÈ fa el
                  sistema i no PER A QUÈ serveix, que és tota la gràcia. */}
              <div className="mt-8 bg-primary-50 border border-primary-200 rounded-md p-4">
                <h3 className="text-sm font-semibold text-primary-900">{t('ssm.pqr')}</h3>
                <p className="text-xs text-primary-800 mt-1 mb-3">{t('ssm.pqrWhy')}</p>
                <div className="space-y-2">
                  {[['p', 'pqrP', 'pqrPh'], ['q', 'pqrQ', 'pqrQh'], ['r', 'pqrR', 'pqrRh']].map(([k, etq, ph]) => (
                    <div key={k}>
                      <label className="block text-xs font-medium text-gray-700 mb-1">{t(`ssm.${etq}`)}</label>
                      <input
                        type="text"
                        value={selectedRD[k] || ''}
                        onChange={(e) => updateRootDefinition({ [k]: e.target.value })}
                        placeholder={t(`ssm.${ph}`)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm
                                   focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">{t('ssm.generated')}</h3>
                  <button
                    onClick={generateRootDefinition}
                    title={t('ssm.generateTitle')}
                    className="px-4 py-2 bg-ok-600 text-white text-sm rounded-md hover:bg-ok-700"
                  >
                    {t('ssm.generate')}
                  </button>
                </div>
                
                {avisGenerar && (
                  <p className="text-xs text-gray-700 bg-white border border-warn-400 rounded-md px-3 py-2">
                    {t('ssm.generateEmpty')}
                  </p>
                )}
                <textarea
                  value={selectedRD.rootDefinition}
                  onChange={(e) => updateRootDefinition({ rootDefinition: e.target.value })}
                  className="w-full p-4 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  rows="4"
                  placeholder={t('catwoe.definition')}
                />
                
                <p className="text-xs text-gray-600">
                  {t('ssm.pqrWhy')}
                  </p>
              </div>
            </div>

            {/* LES CINC E, i no tres.
                Les tres primeres —eficàcia, eficiència, efectivitat— són les clàssiques i eren
                les úniques que hi havia. Checkland n'hi va afegir dues més: **eticitat** («és el
                que moralment toca?») i **elegància** («és una manera bonica de fer-ho?»). Són
                justament les que tothom s'estalvia, i són les que fan que la mesura de rendiment
                no acabi sent només un compte de productivitat. */}
            <div className="bg-white border border-gray-200 rounded-md p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-1">{t('ssm.eTitle')}</h3>
              <p className="text-sm text-gray-600 mb-4">{t('ssm.eWhy')}</p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[['efficacy', 'eEfficacy', 'catwoe.efficacy'],
                  ['efficiency', 'eEfficiency', 'catwoe.efficiency'],
                  ['effectiveness', 'eEffectiveness', 'catwoe.effectiveness'],
                  ['ethicality', 'eEthicality', 'ssm.ethicality'],
                  ['elegance', 'eElegance', 'ssm.elegance']].map(([camp, etq, ph]) => (
                  <div key={camp}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t(`ssm.${etq}`)}
                    </label>
                    <textarea
                      placeholder={t(ph)}
                      value={selectedRD[camp] || ''}
                      onChange={(e) => updateRootDefinition({ [camp]: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded-md text-sm
                                 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      rows="3"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {!showForm && (
        <div className="flex-1 min-w-0 min-h-0 flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <LightBulbIcon className="h-16 w-16 text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t('ssm.pickOne')}
            </h3>
            <p className="text-gray-600 max-w-md">
              Use CATWOE analysis to define relevant systems from your Rich Picture. 
              {t('ssm.pickOneWhy')}
            </p>
          </div>
        </div>
      )}
    </MarcEtapa>
  );
};

export default RootDefinitionsStage;